// ErrorPage (Phase 14) — generic error/500 screen, style-matched to NotFoundPage. Used as the
// router `errorElement` on the authed AppLayout route, so loader/render errors inside the shell
// render here (within the layout) instead of bubbling to the top-level ErrorBoundary. Reads the
// thrown error via useRouteError for a useful detail line.
//
// Stale-chunk case: a lazy route whose chunk 404s after a deploy is not an app bug — reload once
// onto the current build (lib/chunkReload) and show an "updating" notice meanwhile. If the loop
// guard refuses (the reload already happened and it still fails), fall through to the error view.
import { useEffect, useState } from 'react';
import { Link, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { canReloadForStaleChunk, isChunkLoadError, reloadForStaleChunk } from '@/lib/chunkReload';

export default function ErrorPage() {
  const error = useRouteError();
  const staleChunk = isChunkLoadError(error);
  const [reloading] = useState(() => staleChunk && canReloadForStaleChunk());

  useEffect(() => {
    if (reloading) reloadForStaleChunk();
  }, [reloading]);

  if (reloading) {
    return (
      <div className="app-status-page" role="status">
        <h1>Updating Quralyst</h1>
        <p className="text-muted">A new version is available. Loading it now…</p>
      </div>
    );
  }

  let detail = 'An unexpected error occurred. Please try again.';
  if (staleChunk) {
    detail = 'This page could not be loaded. Reload to get the latest version of Quralyst.';
  } else if (isRouteErrorResponse(error)) {
    detail = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error && error.message) {
    detail = error.message;
  }

  return (
    <div className="app-status-page">
      <h1>Something went wrong</h1>
      <p className="text-muted">{detail}</p>
      <div className="d-flex gap-2 justify-content-center">
        <button type="button" className="btn btn-standard" onClick={() => window.location.reload()}>
          Try again
        </button>
        <Link className="btn btn-standard" to={paths.processPreference}>
          Go to Start processing
        </Link>
      </div>
    </div>
  );
}
